import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import textsData from '@/textData/DashboardTexts.json';
import { useUserLanguage } from '@/customHooks';
import { useDashboardFilter } from '@/contexts/DashboardFilterContext';
import {
  OrderDirectionOption,
  PageSizeOption,
  useGetCountryIsoCodesQuery,
  useGetAllCitiesLazyQuery,
  useGetAllStatesLazyQuery,
  useGetPropertiesForContractsQuery,
} from '@/graphql/generated';

const DEBOUNCE_MS = 320;

export const DashboardPropertyFilterBar = () => {
  const userLanguage = useUserLanguage();
  const texts =
    userLanguage === 'es' ? textsData.es.dashboardFilter : textsData.en.dashboardFilter;

  const {
    selectedPropertyId,
    selectedPropertyLabel,
    setPropertySelection,
    geoCountryId,
    geoStateId,
    geoCityId,
    replaceGeoFilter,
    periodYear,
    periodMonth,
    replacePeriodFilter,
  } = useDashboardFilter();

  const [draftYear, setDraftYear] = useState<number | null>(null);
  const [draftMonth, setDraftMonth] = useState<number | null>(null);

  const [draftCountryId, setDraftCountryOnly] = useState<string | null>(null);
  const [draftStateId, setDraftStateOnly] = useState<string | null>(null);
  const [draftCityId, setDraftCityOnly] = useState<string | null>(null);
  const [draftPropertyId, setDraftPropertyId] = useState<string | null>(null);
  const [draftPropertyLabel, setDraftPropertyLabel] = useState<string | null>(null);

  const setDraftCountryId = useCallback((id: string | null) => {
    setDraftCountryOnly(id);
    setDraftStateOnly(null);
    setDraftCityOnly(null);
  }, []);

  const setDraftStateId = useCallback((id: string | null) => {
    setDraftStateOnly(id);
    setDraftCityOnly(null);
  }, []);

  const setDraftCityId = useCallback((id: string | null) => {
    setDraftCityOnly(id);
  }, []);

  const clearDraftGeo = useCallback(() => {
    setDraftCountryOnly(null);
    setDraftStateOnly(null);
    setDraftCityOnly(null);
  }, []);

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [getStates, { data: statesData }] = useGetAllStatesLazyQuery();
  const [getCities, { data: citiesData }] = useGetAllCitiesLazyQuery();
  const { data: countriesData } = useGetCountryIsoCodesQuery();

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, draftCountryId, draftStateId, draftCityId]);

  const openModal = useCallback(() => {
    setDraftCountryOnly(geoCountryId);
    setDraftStateOnly(geoStateId);
    setDraftCityOnly(geoCityId);
    setDraftYear(periodYear);
    setDraftMonth(periodMonth);
    setDraftPropertyId(selectedPropertyId);
    setDraftPropertyLabel(selectedPropertyLabel);
    setSearchInput('');
    setDebouncedSearch('');
    setPage(1);
    setOpen(true);
  }, [
    geoCountryId,
    geoStateId,
    geoCityId,
    periodYear,
    periodMonth,
    selectedPropertyId,
    selectedPropertyLabel,
  ]);

  const commitFiltersFromDraft = useCallback(() => {
    replaceGeoFilter(draftCountryId, draftStateId, draftCityId);
    replacePeriodFilter(draftYear, draftMonth);
  }, [
    draftCountryId,
    draftStateId,
    draftCityId,
    draftYear,
    draftMonth,
    replaceGeoFilter,
    replacePeriodFilter,
  ]);

  const handleModalCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleModalAccept = useCallback(() => {
    commitFiltersFromDraft();
    setPropertySelection(draftPropertyId, draftPropertyLabel);
    setOpen(false);
  }, [commitFiltersFromDraft, draftPropertyId, draftPropertyLabel, setPropertySelection]);

  const currentCalendarYear = new Date().getFullYear();
  const yearSelectOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = currentCalendarYear; y >= currentCalendarYear - 15; y -= 1) {
      years.push(y);
    }
    return years;
  }, [currentCalendarYear]);

  const locale = userLanguage === 'es' ? 'es' : 'en-US';
  const monthSelectOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const m = i + 1;
        const label = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
          new Date(2000, m - 1, 1),
        );
        return { value: m, label };
      }),
    [locale],
  );

  const periodSummaryLine = useMemo(() => {
    if (periodYear == null) return null;
    if (periodMonth == null) {
      return texts.periodSummaryYear.replace('{{year}}', String(periodYear));
    }
    const monthName = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
      new Date(periodYear, periodMonth - 1, 1),
    );
    return texts.periodSummaryMonthYear
      .replace('{{month}}', monthName)
      .replace('{{year}}', String(periodYear));
  }, [periodYear, periodMonth, locale, texts]);

  const handleDraftYearChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === '') {
      setDraftYear(null);
      setDraftMonth(null);
      return;
    }
    setDraftYear(Number(v));
  }, []);

  const handleDraftMonthChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === '') {
      setDraftMonth(null);
      return;
    }
    setDraftMonth(Number(v));
  }, []);

  const clearDraftPeriod = useCallback(() => {
    setDraftYear(null);
    setDraftMonth(null);
  }, []);

  const showClearPeriod = draftYear != null;

  useEffect(() => {
    if (!open || !draftCountryId) return;
    void getStates({ variables: { countryCode: draftCountryId } });
  }, [open, draftCountryId, getStates]);

  useEffect(() => {
    if (!open || !draftStateId) return;
    void getCities({ variables: { stateCode: draftStateId } });
  }, [open, draftStateId, getCities]);

  const handleCountryChange = useCallback(
    (value: string | null) => {
      const id = value === '' || value == null ? null : value;
      setDraftCountryId(id);
      if (id) void getStates({ variables: { countryCode: id } });
    },
    [getStates, setDraftCountryId],
  );

  const handleStateChange = useCallback(
    (value: string | null) => {
      const id = value === '' || value == null ? null : value;
      setDraftStateId(id);
      if (id) void getCities({ variables: { stateCode: id } });
    },
    [getCities, setDraftStateId],
  );

  const handleCityChange = useCallback(
    (value: string | null) => {
      const id = value === '' || value == null ? null : value;
      setDraftCityId(id);
    },
    [setDraftCityId],
  );

  const stateOptions =
    statesData?.states.map((state) => ({
      name: state.name,
      value: state.geonamesAdmin1Code,
    })) ?? [];

  const cityOptions =
    citiesData?.cities.map((city) => ({
      name: city.name,
      value: city.geonamesId,
    })) ?? [];

  const countryOptions =
    countriesData?.countries.map((country) => ({
      name: country.name,
      value: country.iso2,
    })) ?? [];

  const { data, loading } = useGetPropertiesForContractsQuery({
    variables: {
      input: {
        pageSize: PageSizeOption.TwentyFive,
        page: String(page),
        orderDirection: OrderDirectionOption.Asc,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(draftCountryId ? { countryId: draftCountryId } : {}),
        ...(draftStateId ? { stateId: draftStateId } : {}),
        ...(draftCityId ? { cityId: draftCityId } : {}),
      },
    },
    skip: !open,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleModalCancel();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, handleModalCancel]);

  const items = data?.properties?.items ?? [];
  const pagination = data?.properties?.pagination;
  const totalPages = Math.max(1, pagination?.totalPages ?? 1);
  const currentPage = pagination?.currentPage ?? page;
  const hasNext = pagination?.hasNextPage ?? false;
  const hasPrev = pagination?.hasPreviousPage ?? false;

  const pageLabel = texts.pageOf
    .replace('{{current}}', String(currentPage))
    .replace('{{total}}', String(totalPages));

  const showClearGeo = Boolean(draftCountryId || draftStateId || draftCityId);

  const modal =
    open &&
    createPortal(
      <div
        role="presentation"
        onClick={handleModalCancel}
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-[rgba(15,23,42,0.42)] p-3 backdrop-blur-[12px]"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-property-picker-title"
          onClick={(e) => e.stopPropagation()}
          className="flex h-[min(90vh,880px)] w-[min(92vw,720px)] flex-col overflow-hidden rounded-[18px] bg-white shadow-[0_24px_48px_rgba(15,23,42,0.22)]"
        >
          <div id="dashboard-property-picker-title" className="shrink-0 border-b border-b-[#E5E7EB] px-6 py-[1.1rem] font-[Montserrat,Arial,sans-serif] text-[1.05rem] font-bold text-[#1F2937]">{texts.selectProperty}</div>
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div className="shrink-0 px-6 pb-[1.15rem] pt-[0.85rem]">
            <div className="grid grid-cols-1 gap-3 md:gap-4">
              <div className="min-w-0">
                <label
                  className="mb-[0.35rem] block font-[Montserrat,Arial,sans-serif] text-[0.85rem] font-semibold text-[#1F2937]"
                  htmlFor="dashboard-geo-country"
                >
                  {texts.countryLabel}
                </label>
                <select
                  id="dashboard-geo-country"
                  className="box-border w-full cursor-pointer rounded-[10px] border border-[#E5E7EB] bg-white px-[0.85rem] py-[0.65rem] font-[Montserrat,Arial,sans-serif] text-[0.9rem] text-[#1F2937] focus:border-[rgba(11,52,101,0.45)] focus:outline-none"
                  value={draftCountryId ?? ''}
                  onChange={(e) => handleCountryChange(e.target.value || null)}
                >
                  <option value="">{texts.countryPlaceholder}</option>
                  {countryOptions.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label
                  className="mb-[0.35rem] block font-[Montserrat,Arial,sans-serif] text-[0.85rem] font-semibold text-[#1F2937]"
                  htmlFor="dashboard-geo-state"
                >
                  {texts.stateLabel}
                </label>
                <select
                  id="dashboard-geo-state"
                  className="box-border w-full cursor-pointer rounded-[10px] border border-[#E5E7EB] bg-white px-[0.85rem] py-[0.65rem] font-[Montserrat,Arial,sans-serif] text-[0.9rem] text-[#1F2937] focus:border-[rgba(11,52,101,0.45)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-55"
                  value={draftStateId ?? ''}
                  onChange={(e) => handleStateChange(e.target.value || null)}
                  disabled={!draftCountryId}
                >
                  <option value="">{texts.statePlaceholder}</option>
                  {stateOptions.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label
                  className="mb-[0.35rem] block font-[Montserrat,Arial,sans-serif] text-[0.85rem] font-semibold text-[#1F2937]"
                  htmlFor="dashboard-geo-city"
                >
                  {texts.cityLabel}
                </label>
                <select
                  id="dashboard-geo-city"
                  className="box-border w-full cursor-pointer rounded-[10px] border border-[#E5E7EB] bg-white px-[0.85rem] py-[0.65rem] font-[Montserrat,Arial,sans-serif] text-[0.9rem] text-[#1F2937] focus:border-[rgba(11,52,101,0.45)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-55"
                  value={draftCityId ?? ''}
                  onChange={(e) => handleCityChange(e.target.value || null)}
                  disabled={!draftStateId}
                >
                  <option value="">{texts.cityPlaceholder}</option>
                  {cityOptions.map((city) => (
                    <option key={city.value} value={city.value}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {showClearGeo ? (
              <button className="mb-3 mt-2 border-none bg-transparent p-0 font-[Montserrat,Arial,sans-serif] text-[0.82rem] font-semibold text-[#4A9462] underline hover:opacity-85" type="button" onClick={() => clearDraftGeo()}>
                {texts.clearLocation}
              </button>
            ) : null}
          </div>
          <div className="mt-[0.35rem] shrink-0 border-y border-y-[#E5E7EB] bg-[#F8FAFC] px-6 pb-4 pt-[1.1rem]">
            <div className="mb-2 font-[Montserrat,Arial,sans-serif] text-[0.88rem] font-bold text-[#1F2937]">{texts.periodSection}</div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="min-w-0">
                <label className="mb-[0.35rem] block font-[Montserrat,Arial,sans-serif] text-[0.85rem] font-semibold text-[#1F2937]" htmlFor="dashboard-period-year">
                  {texts.periodYearLabel}
                </label>
                <select
                  className="box-border w-full cursor-pointer rounded-[10px] border border-[#E5E7EB] bg-white px-[0.85rem] py-[0.65rem] font-[Montserrat,Arial,sans-serif] text-[0.9rem] text-[#1F2937] focus:border-[rgba(11,52,101,0.45)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-55"
                  id="dashboard-period-year"
                  value={draftYear != null ? String(draftYear) : ''}
                  onChange={handleDraftYearChange}
                  aria-label={texts.periodYearLabel}
                >
                  <option value="">{texts.noPeriodYear}</option>
                  {yearSelectOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className="mb-[0.35rem] block font-[Montserrat,Arial,sans-serif] text-[0.85rem] font-semibold text-[#1F2937]" htmlFor="dashboard-period-month">
                  {texts.periodMonthLabel}
                </label>
                <select
                  className="box-border w-full cursor-pointer rounded-[10px] border border-[#E5E7EB] bg-white px-[0.85rem] py-[0.65rem] font-[Montserrat,Arial,sans-serif] text-[0.9rem] text-[#1F2937] focus:border-[rgba(11,52,101,0.45)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-55"
                  id="dashboard-period-month"
                  value={draftMonth != null ? String(draftMonth) : ''}
                  onChange={handleDraftMonthChange}
                  disabled={draftYear == null}
                  aria-label={texts.periodMonthLabel}
                >
                  <option value="">{texts.wholeYearOption}</option>
                  {monthSelectOptions.map(({ value: m, label }) => (
                    <option key={m} value={m}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {showClearPeriod ? (
              <button className="mb-3 mt-2 border-none bg-transparent p-0 font-[Montserrat,Arial,sans-serif] text-[0.82rem] font-semibold text-[#4A9462] underline hover:opacity-85" type="button" onClick={() => clearDraftPeriod()}>
                {texts.clearPeriod}
              </button>
            ) : null}
          </div>
          <div className="shrink-0 px-6 pb-0 pt-[0.85rem]">
            <input
              className="box-border w-full rounded-[10px] border border-[#E5E7EB] bg-white px-[0.85rem] py-[0.65rem] font-[Montserrat,Arial,sans-serif] text-[0.9rem] text-[#1F2937] placeholder:text-[#6B7280] placeholder:opacity-85 focus:border-[rgba(11,52,101,0.45)] focus:outline-none"
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={texts.searchPlaceholder}
              autoComplete="off"
              aria-label={texts.searchPlaceholder}
            />
          </div>
          <ul className="m-0 list-none px-0 pb-3 pt-2">
            {draftPropertyId == null ? (
              <button
                className="w-full bg-[rgba(74,148,98,0.12)] px-6 py-3 text-left font-[Montserrat,Arial,sans-serif] text-[0.95rem] font-bold text-[#4A9462]"
                type="button"
                onClick={() => {
                  setDraftPropertyId(null);
                  setDraftPropertyLabel(null);
                }}
              >
                {texts.allProperties}
              </button>
            ) : (
              <button
                className="w-full bg-transparent px-6 py-3 text-left font-[Montserrat,Arial,sans-serif] text-[0.95rem] font-medium text-[#1F2937] hover:bg-[#F8FAFC]"
                type="button"
                onClick={() => {
                  setDraftPropertyId(null);
                  setDraftPropertyLabel(null);
                }}
              >
                {texts.allProperties}
              </button>
            )}
            {loading ? (
              <button className="w-full bg-transparent px-6 py-3 text-left font-[Montserrat,Arial,sans-serif] text-[0.95rem] font-medium text-[#1F2937]" type="button" disabled>
                …
              </button>
            ) : (
              items.map((p) => {
                const active = p.id === draftPropertyId;
                return (
                  <button
                    key={p.id}
                    className={`w-full px-6 py-3 text-left font-[Montserrat,Arial,sans-serif] text-[0.95rem] ${active ? 'bg-[rgba(74,148,98,0.12)] font-bold text-[#4A9462]' : 'bg-transparent font-medium text-[#1F2937] hover:bg-[#F8FAFC]'}`}
                    type="button"
                    onClick={() => {
                      setDraftPropertyId(p.id);
                      setDraftPropertyLabel(p.name);
                    }}
                  >
                    {p.name}
                  </button>
                );
              })
            )}
          </ul>
          <div className="flex shrink-0 items-center justify-center border-t border-t-[#E5E7EB] px-6 pb-4 pt-[0.85rem]">
            <div className="flex items-center justify-center gap-3">
              <button
                className="inline-flex h-8 min-w-8 shrink-0 items-center justify-center rounded-[10px] border border-[#CED3DE] bg-white p-0 text-[1.25rem] leading-none text-[#222B45] hover:border-[#7E8AA2] hover:bg-[#BEC4D1] disabled:cursor-not-allowed disabled:opacity-45"
                type="button"
                disabled={!hasPrev || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label={texts.previousPage}
              >
                ‹
              </button>
              <span className="min-w-32 text-center font-[Montserrat,Arial,sans-serif] text-[0.8rem] font-semibold text-[#6B7280]">{pageLabel}</span>
              <button
                className="inline-flex h-8 min-w-8 shrink-0 items-center justify-center rounded-[10px] border border-[#CED3DE] bg-white p-0 text-[1.25rem] leading-none text-[#222B45] hover:border-[#7E8AA2] hover:bg-[#BEC4D1] disabled:cursor-not-allowed disabled:opacity-45"
                type="button"
                disabled={!hasNext || loading}
                onClick={() => setPage((p) => p + 1)}
                aria-label={texts.nextPage}
              >
                ›
              </button>
            </div>
          </div>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-[0.65rem] border-t border-t-[#E5E7EB] bg-white px-6 pb-[1.1rem] pt-[0.9rem]">
            <button className="rounded-[10px] border border-[#E5E7EB] bg-white px-[1.1rem] py-2 font-[Montserrat,Arial,sans-serif] text-[0.88rem] font-semibold text-[#1F2937] hover:bg-[#F8FAFC]" type="button" onClick={handleModalCancel}>
              {texts.cancelModal}
            </button>
            <button className="rounded-[10px] border-none bg-[#4A9462] px-[1.1rem] py-2 font-[Montserrat,Arial,sans-serif] text-[0.88rem] font-semibold text-white hover:brightness-105" type="button" onClick={handleModalAccept}>
              {texts.acceptModal}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white px-5 py-4 shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="m-0 font-[Montserrat,Arial,sans-serif] text-[1.05rem] font-bold text-[#1F2937]">{texts.title}</h2>
          {periodSummaryLine ? <span className="font-[Montserrat,Arial,sans-serif] text-[0.78rem] font-semibold text-[#6B7280]">{periodSummaryLine}</span> : null}
        </div>
        <button className="flex min-w-[200px] cursor-pointer items-center justify-between gap-2 rounded-[10px] border border-[#E5E7EB] bg-[#F8FAFC] px-[0.9rem] py-[0.55rem] font-[Montserrat,Arial,sans-serif] text-[0.9rem] font-semibold text-[#1F2937] hover:border-[rgba(11,52,101,0.35)]" type="button" onClick={openModal}>
          <span>{selectedPropertyLabel ?? texts.selectProperty}</span>
          <span aria-hidden className="shrink-0 text-[0.65rem] opacity-55">▼</span>
        </button>
      </div>
      {modal}
    </>
  );
};
